import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import { OWNER_ROLES } from '../../preboarding-tasks/config';
import { positionAsync } from '../../positions/config';

const usePbTaskTemplatesApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/preboarding-task-templates?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // Form value (parsed items array) ↔ API value (ItemsJson string). Items with
  // an empty title are silently dropped — the editor always appends one blank
  // row template users may leave unfilled.
  const toPayload = useCallback((formData) => ({
    name: formData.name,
    departmentId: formData.departmentId || null,
    positionId: formData.positionId || null,
    isDefault: !!formData.isDefault,
    itemsJson: JSON.stringify(
      (formData.items || [])
        .filter((it) => (it.title || '').trim() !== '')
        .map((it) => ({
          key: it.key,
          title: it.title.trim(),
          ownerRole: it.ownerRole || 'hr',
          slaHours: Number(it.slaHours) || 0,
          isMandatory: !!it.isMandatory,
          requiresHrApproval: !!it.requiresHrApproval,
          dependsOn: it.dependsOn || [],
          documentTypes: Array.isArray(it.documentTypes) ? it.documentTypes : [],
        })),
    ),
  }), []);

  const toFormValues = useCallback((row) => {
    let items = [];
    try {
      const parsed = JSON.parse(row.itemsJson || '[]');
      if (Array.isArray(parsed)) {
        items = parsed.map((it, i) => ({
          key: it.key || `task-${i + 1}`,
          title: it.title || '',
          ownerRole: it.ownerRole || 'hr',
          slaHours: it.slaHours ?? 0,
          isMandatory: !!it.isMandatory,
          requiresHrApproval: !!it.requiresHrApproval,
          dependsOn: Array.isArray(it.dependsOn) ? it.dependsOn : [],
          // documentTypes string|object karışık olabilir (BE her ikisini kabul
          // eder); editör string listesiyle çalışır.
          documentTypes: Array.isArray(it.documentTypes)
            ? it.documentTypes.map((d) => (typeof d === 'string' ? d : d?.type)).filter(Boolean)
            : [],
        }));
      }
    } catch {
      items = [];
    }
    return {
      id: row.id,
      name: row.name,
      departmentId: row.departmentId || null,
      positionId: row.positionId || null,
      isDefault: !!row.isDefault,
      items,
    };
  }, []);

  const create = useCallback(async (formData) => {
    try { await api.post('/v1/preboarding-task-templates', toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const update = useCallback(async (id, formData) => {
    try { await api.put(`/v1/preboarding-task-templates/${id}`, toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const remove = useCallback(async (id) => {
    try { await api.delete(`/v1/preboarding-task-templates/${id}`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name', label: t('preboardingTaskTemplate.name'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'isDefault', label: t('preboardingTaskTemplate.isDefault'),
      options: { filter: true, sort: true, customBodyRender: (v) => v ? t('common.yes') : t('common.no') },
    },
    numericFilterColumn('itemCount', t('preboardingTaskTemplate.itemCount'), { decimals: 0 }),
  ], [t]);

  const objectSchema = useMemo(() => ([
    { name: 'name', label: t('preboardingTaskTemplate.form.name'), type: 'text', required: true },
    {
      name: 'departmentId', label: t('preboardingTaskTemplate.form.department'), type: 'asyncSelect', grid: 6,
      description: t('preboardingTaskTemplate.form.scopeHint'),
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/departments', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((d) => ({ id: d.id, name: d.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/departments/${id}`);
        const d = res.data?.data || {};
        return { id, name: d.name || '' };
      },
    },
    {
      name: 'positionId', label: t('preboardingTaskTemplate.form.position'), type: 'asyncSelect', grid: 6,
      // Pozisyonun GÖRÜNEN ADI YOKTUR: /v1/positions -> PositionResponse yalnız
      // Code/DepartmentName/TitleName taşır (`name` alanı hiç yok) ve filtre sözleşmesi
      // `name[like]` tanımaz. Etiket ile arama parametresini burada elle kurmak yerine
      // deponun tek pozisyon seçici yardımcısı kullanılır.
      ...positionAsync,
      searchPlaceholder: t('position.pickerSearchPlaceholder'),
    },
    {
      name: 'isDefault', label: t('preboardingTaskTemplate.form.isDefault'), type: 'checkbox',
      description: t('preboardingTaskTemplate.form.isDefaultHint'),
    },
    {
      name: 'items', label: t('preboardingTaskTemplate.form.items'), type: 'taskTemplateItems',
      ownerRoles: OWNER_ROLES,
      // "Bağlı Belgeler": seçilen türler süreç açılışında otomatik belge
      // gereksinimi yaratır; bu belgeler yüklenmeden görev tamamlanamaz.
      documentTypeOptions: [
        'employment_contract', 'nda', 'id_card', 'residence', 'iban',
        'sgk_form', 'emergency_contact', 'vehicle', 'private_health',
      ].map((dt) => ({ id: dt, name: t(`preboardingDoc.type.${dt}`, { defaultValue: dt }) })),
    },
  ]), [t]);

  return { fetching, data, pagination, columns, objectSchema, toFormValues, refresh, create, update, remove };
};

export default usePbTaskTemplatesApi;
